import React, { useMemo, useState } from 'react'
import {
  childrenItemViewLabels,
  spaceTypePluralLabels,
  spaceTypeSingularLabels,
} from '~/packages/general/variable/spaceTime.data'
import styled from 'styled-components'
import { Layer } from '~/packages/general/variable/spaceTime.types'
import ViewSelectModalVariable from './viewSelectModalVariable.component'
import ViewSelectModalSpace from './viewSelectModalSpace.component'
import ViewSelectModalTime from './viewSelectModalTime.component'
import { VariableWithConclusion } from '~/packages/general/variable/variables.typing'
import { ReportWithLayer } from './reports'
import { Expression } from '../../prix/types/expression'
import { ExpressionOperationAvailable } from '../../prix/types/expression'
import { buildFilterPropertyOptions, FilterableByShape } from './filterableByField.util'
import { isVariableLayer } from './clusterListInput.util'
import useAPI from '~/prix/react/hooks/api'
import {
  getPreferredSpaceFromGeneralSettings,
  isSameAsPreferredDelimitation,
} from './userDelimitationScope.util'

const ViewSelectDesktopBoxWrapper = styled.div<{ $isDisabled: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: row;

  small,
  span,
  h2 {
    color: #363636;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    flex-direction: column;
    max-width: none;
  }

  button {
    display: flex;
    flex-direction: column;
    background: transparent;
    box-shadow: none;
    border: none;
    border-radius: 0;
    padding: 1rem;
    cursor: pointer;
    justify-content: flex-start;
    transition-duration: ${({ theme }) => theme.transitionDurationMs}ms;
    transition-timing-function: ${({ theme }) => theme.transitionTimingFunction};
    transition-property: background-color, box-shadow;
    text-align: left;

    border-bottom: 1px solid #cacaca;
    cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ $isDisabled }) => ($isDisabled ? 0.5 : 1)};
    transition: opacity 0.3s ease;

    &:hover {
      background-color: #fff;
      box-shadow: ${({ theme }) => theme.shadow(2)};
      z-index: 1;
    }

    &:first-child {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
      border-right: 0.5px solid rgba(0, 0, 0, 0.1);

      @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
        border-top-right-radius: 8px;
        border-bottom-left-radius: 0px;
      }
    }

    &:last-child {
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
      border-left: 0.5px solid rgba(0, 0, 0, 0.1);

      @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
        border-top-right-radius: 0px;
        border-bottom-left-radius: 8px;
      }
    }

    &:not(:first-child):not(:last-child) {
      border-left: 0.5px solid rgba(0, 0, 0, 0.1);
      border-right: 0.5px solid rgba(0, 0, 0, 0.1);
    }

    h2 {
      font-size: 0.7rem;
      font-weight: 700;
      margin: 0;
      margin-bottom: ${({ theme }) => theme.padding(0.3)};
      text-transform: uppercase;
    }
  }

  .column {
    flex: 1;
  }
`

function hasFilterableByShape(
  filterableBy: unknown,
): filterableBy is { shape: FilterableByShape } {
  return (
    !!filterableBy &&
    typeof filterableBy === 'object' &&
    'shape' in filterableBy &&
    !!filterableBy.shape &&
    typeof filterableBy.shape === 'object'
  )
}

function extractAppliedFilters(filter: Expression | undefined, filterableBy?: unknown): string[] {
  if (!filter || !hasFilterableByShape(filterableBy)) return []
  const filters: string[] = []

  const possibleProperties = buildFilterPropertyOptions(filterableBy.shape)

  const getBooleanLabels = () => [
    { value: { key: true, kind: 'boolean', type: 'boolean' }, label: 'Verdadeiro' },
    { value: { key: false, kind: 'boolean', type: 'boolean' }, label: 'Falso' },
  ]

  const checkOperationsAvailable = (operation: 'number' | 'string' | 'boolean' | undefined) => {
    return (
      ExpressionOperationAvailable[operation as keyof typeof ExpressionOperationAvailable] || []
    )
  }

  const isBooleanLiteral = (expr: Expression | undefined) =>
    expr?.kind === 'literal' &&
    (expr.literalType === 'boolean' ||
      expr.subType === 'boolean' ||
      typeof expr.value === 'boolean')

  const extractFilterLabel = (expr: Expression): string | null => {
    if (expr.kind !== 'binary' || expr.left?.kind !== 'property') return null

    const propertyKey = String(expr.left.property)

    const propertyInfo = possibleProperties.find(prop => prop.value.key === propertyKey)
    if (!propertyInfo) return null

    const possibleOperators = checkOperationsAvailable(
      propertyInfo.value.type === 'string' ||
        propertyInfo.value.type === 'number' ||
        propertyInfo.value.type === 'boolean'
        ? propertyInfo.value.type
        : undefined,
    )
    const operatorInfo = possibleOperators.find(op => op?.value?.key === expr.operator)
    const operatorLabel = operatorInfo?.label || expr.operator

    let valueLabel = ''
    if (expr.right?.kind === 'literal') {
      const rightValue = expr.right.value
      const keysAndLabels = propertyInfo.value.keysAndLabels

      if (expr.right.subType === 'enumerated' && keysAndLabels) {
        valueLabel = keysAndLabels[rightValue as string] || String(rightValue)
      } else if (isBooleanLiteral(expr.right)) {
        const booleanLabels = getBooleanLabels()
        const booleanInfo = booleanLabels.find(
          option => String(option.value.key) === String(rightValue),
        )
        valueLabel = booleanInfo?.label || String(rightValue)
      } else {
        valueLabel = String(rightValue)
      }
    }

    if (expr.operator === '=') {
      return valueLabel
    } else {
      return `${operatorLabel} ${valueLabel}`
    }
  }

  function processExpression(expr: Expression) {
    if (expr.kind === 'binary') {
      if (expr.operator === 'AND' || expr.operator === 'OR') {
        processExpression(expr.left)
        processExpression(expr.right)
      } else {
        const filterLabel = extractFilterLabel(expr)
        if (filterLabel) {
          filters.push(filterLabel)
        }
      }
    } else if (expr.kind === 'list') {
      expr.items?.forEach(item => processExpression(item))
    }
  }

  processExpression(filter)

  return [...new Set(filters)]
}

export interface ViewSelectTopBoxDesktopProps {
  variables: VariableWithConclusion[]
  layer: Layer
  baseLayer: Layer
  onChangeLayer: (layer: Layer | ((oldLayer: Layer) => Layer)) => void
  onChangeReport: (report: ReportWithLayer | null) => void
  stateId: number | null
  stateName: string | null
  subtitle?: string | null
  isDisabled: boolean
}

export default function ViewSelectTopBoxDesktop({
  layer,
  baseLayer,
  variables,
  onChangeLayer,
  onChangeReport,
  stateId,
  stateName,
  subtitle,
  isDisabled,
}: ViewSelectTopBoxDesktopProps) {
  if (!isVariableLayer(layer) || !isVariableLayer(baseLayer)) {
    return null
  }

  const { context } = useAPI()
  const preferredSpace = getPreferredSpaceFromGeneralSettings(context.user?.activeClient?.settings?.general)

  const [isModalVariableOpen, setIsModalVariableOpen] = useState(false)
  const [isModalTimeOpen, setIsModalTimeOpen] = useState(false)
  const [isModalSpaceOpen, setIsModalSpaceOpen] = useState(false)

  const appliedFilters = useMemo(() => {
    return extractAppliedFilters(baseLayer.variable.filter, baseLayer.variable.filterableBy)
  }, [baseLayer.variable.filter, baseLayer.variable.key, baseLayer.variable.presetKey])
  const isGeneralLabelOnly = layer.variable.label?.trim().toLowerCase() === 'geral'
  const hasLabel = Boolean(layer.variable.label) && !isGeneralLabelOnly
  const hasTitle = Boolean(layer.variable.title)
  const mainVariableLabel = hasLabel
    ? layer.variable.label!
    : layer.variable.title || layer.variable.label || ''
  const secondaryVariableTitle =
    hasLabel && hasTitle && layer.variable.title !== layer.variable.label
      ? layer.variable.title
      : null
  const normalizedMainVariableLabel = mainVariableLabel.trim().toLowerCase()
  const normalizedSecondaryVariableTitle = secondaryVariableTitle?.trim().toLowerCase() || null
  const nonRedundantAppliedFilters = appliedFilters.filter(filterLabel => {
    const normalizedFilterLabel = filterLabel.trim().toLowerCase()
    return (
      normalizedFilterLabel !== normalizedMainVariableLabel &&
      normalizedFilterLabel !== normalizedSecondaryVariableTitle
    )
  })

  let spaceChildrenLabel: string
  if (layer.space.childrenType === 'custom') {
    spaceChildrenLabel = layer.space.childrenTitle
  } else if (layer.space.childrenType === 'item') {
    spaceChildrenLabel =
      childrenItemViewLabels[layer.space.childrenItemView! as keyof typeof childrenItemViewLabels]
  } else if (layer.space.childrenType === layer.space.delimitationType) {
    spaceChildrenLabel = spaceTypeSingularLabels[layer.space.childrenType]
  } else {
    spaceChildrenLabel = spaceTypePluralLabels[layer.space.childrenType]
  }

  const subtitleSplitted = useMemo(() => subtitle?.split('-').pop(), [subtitle])
  const isViewingOwnDelimitation = isSameAsPreferredDelimitation(layer.space, preferredSpace)

  return (
    <React.Fragment>
      <ViewSelectDesktopBoxWrapper $isDisabled={isDisabled}>
        <button
          className='column variable-characteristic'
          onClick={() => {
            if (isDisabled) return
            setIsModalVariableOpen(true)
          }}
        >
          <h2>O quê</h2>
          <span>{mainVariableLabel}</span>
          {nonRedundantAppliedFilters.length > 0 ? (
            <small>Filtros: {nonRedundantAppliedFilters.join(', ')}</small>
          ) : (
            <small>{secondaryVariableTitle}</small>
          )}
        </button>
        <button
          className='column time'
          onClick={() => {
            if (isDisabled) return
            setIsModalTimeOpen(true)
          }}
        >
          <h2>Quando</h2>
          <span>{layer.time.title}</span>
        </button>
        <button
          className='column space'
          onClick={() => {
            if (isDisabled) return
            setIsModalSpaceOpen(true)
          }}
        >
          <h2>Onde</h2>
          <span>
            {isViewingOwnDelimitation ? 'Todos os dados' : layer.space.delimitationTitle}
            {!isViewingOwnDelimitation &&
            layer.space.delimitationType === 'municipality' &&
            subtitleSplitted !== undefined
              ? `- ${subtitleSplitted}`
              : null}
          </span>
          <small>{spaceChildrenLabel}</small>
        </button>
      </ViewSelectDesktopBoxWrapper>

      <ViewSelectModalVariable
        variables={variables}
        isOpen={isModalVariableOpen}
        onClose={() => setIsModalVariableOpen(false)}
        layer={layer}
        baseLayer={baseLayer}
        onChangeLayer={onChangeLayer}
        stateId={stateId}
        stateName={stateName}
      />

      <ViewSelectModalSpace
        layer={layer}
        isOpen={isModalSpaceOpen}
        onClose={() => setIsModalSpaceOpen(false)}
        onChangeLayer={onChangeLayer}
      />

      <ViewSelectModalTime
        layer={layer}
        isOpen={isModalTimeOpen}
        onClose={() => setIsModalTimeOpen(false)}
        onChangeLayer={onChangeLayer}
        onChangeReport={onChangeReport}
      />
    </React.Fragment>
  )
}
